import { useQuery } from '@tanstack/react-query';

import { getVocabulary, type VocabularyOption, type VocabularyResponse } from '../api';

/**
 * The controlled vocabularies, fetched once and shared by every form, filter
 * and label in the Asset Library.
 *
 * There is deliberately no static fallback list anywhere in the frontend. The
 * option lists used to be mirrored in `constants/taxonomy.ts` with a comment
 * asking people to keep the two in sync; the backend's `src/core/vocabulary.py`
 * is the only definition now, and this hook is how the UI reads it. Nameplate,
 * carline code and business unit are three independent lists: nothing is
 * derived or suggested between them.
 *
 * Cached for the session: the lists change only on deploy, and a user with a
 * tab open across a deploy sees the new lists on their next full load, which
 * is acceptable for option lists.
 */
export const EMPTY_VOCABULARY: VocabularyResponse = {
  nameplates: [],
  carline_codes: [],
  business_units: [],
  tiers: [],
  ai_indicators: [],
  source_agencies: [],
  domains: [],
  channel_rights: [],
  format_types: [],
  film_format_sizes: [],
  film_durations: [],
  countries: [],
  regions: [],
};

export const VOCABULARY_QUERY_KEY = ['asset-library', 'vocabulary'] as const;

export interface VocabularyState {
  vocabulary: VocabularyResponse;
  isLoading: boolean;
  /**
   * The fetch failed and `vocabulary` is the empty fallback. Callers that
   * render option lists must say so — an empty select with no message is
   * indistinguishable from a form that has nothing to offer.
   */
  isError: boolean;
  /** Try the fetch again, for a "reload" affordance next to the error. */
  retry: () => void;
}

/**
 * A response coerced to the full shape, whatever came back.
 *
 * Every list the UI maps over is guaranteed to be an array. Without this, a
 * `{}` or a partial payload — a proxy answering with an empty body, a stale
 * backend, a test double that forgot a key — reaches `vocabulary.nameplates
 * .map(...)` and takes the whole Asset Library page down, which is a far worse
 * failure than an empty select. Exported for the unit test.
 */
export function normaliseVocabulary(data: unknown): VocabularyResponse {
  const source = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const list = <T>(key: keyof VocabularyResponse): T[] => {
    const value = source[key];
    return Array.isArray(value) ? (value as T[]) : [];
  };
  return {
    nameplates: list('nameplates'),
    carline_codes: list('carline_codes'),
    business_units: list('business_units'),
    tiers: list('tiers'),
    ai_indicators: list('ai_indicators'),
    source_agencies: list('source_agencies'),
    domains: list('domains'),
    channel_rights: list('channel_rights'),
    format_types: list('format_types'),
    film_format_sizes: list('film_format_sizes'),
    film_durations: list('film_durations'),
    countries: list('countries'),
    regions: list('regions'),
  };
}

export function useVocabulary(): VocabularyState {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: VOCABULARY_QUERY_KEY,
    queryFn: getVocabulary,
    select: normaliseVocabulary,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
  return {
    vocabulary: data ?? EMPTY_VOCABULARY,
    isLoading,
    isError,
    retry: () => void refetch(),
  };
}

/**
 * The stored token rendered for display, across every vocabulary list.
 *
 * Falls back to the raw value rather than blanking it: a row written before an
 * option was renamed should still show what it holds instead of appearing
 * empty. `constants/taxonomy.ts`'s `taxonomyLabel` does the same for the
 * UI-only lists (format, frame rate, platforms); this one covers the lists
 * the backend owns.
 */
export function vocabularyLabel(
  vocabulary: VocabularyResponse,
  value: string | null | undefined,
): string {
  if (!value) return '';
  const lists: VocabularyOption[][] = [
    vocabulary.nameplates,
    vocabulary.carline_codes,
    vocabulary.business_units,
    vocabulary.tiers,
    vocabulary.ai_indicators,
    vocabulary.source_agencies,
    vocabulary.domains,
    vocabulary.channel_rights,
    vocabulary.format_types,
    vocabulary.countries,
    vocabulary.regions,
  ];
  for (const options of lists) {
    const match = options.find((option) => option.value === value);
    if (match) return match.label;
  }
  return value;
}

/** `vocabularyLabel` over a list, joined for a caption or a read-only row. */
export function vocabularyLabels(
  vocabulary: VocabularyResponse,
  values: readonly string[] | null | undefined,
): string {
  return (values ?? []).map((value) => vocabularyLabel(vocabulary, value)).join(', ');
}
